/**
 * Shared hook for thumbnail sprite VTT parsing
 *
 * Used by both ThumbnailScrubber (admin) and TimelineThumbnailSprite (player)
 * to fetch and parse VTT sprite sheet data.
 *
 * Supports dual-quality sprites:
 * - 'standard' (320×180) - Used by player timeline for fast loading
 * - 'hd' (480×270) - Used by admin modals for better visual quality
 */

import { useCallback, useEffect, useState } from "react";

const DEFAULT_SPRITE_COLUMNS = 12;

/** Quality levels for thumbnail sprites */
export type ThumbnailQuality = "standard" | "hd";

/**
 * Parsed VTT cue with time range and sprite coordinates
 */
export type ThumbnailCue = {
  index: number;
  startTime: number; // seconds
  endTime: number; // seconds
  imageUrl: string;
  coords: {
    x: number;
    y: number;
    w: number;
    h: number;
  };
};

/**
 * Parse VTT timestamp to seconds
 * Formats: "00:00:10.000" or "01:30:15.500" or "0:10.000"
 */
function parseTimestamp(timestamp: string): number {
  const parts = timestamp.split(":");
  if (parts.length === 3) {
    return (
      Number.parseInt(parts[0] || "0", 10) * 3600 +
      Number.parseInt(parts[1] || "0", 10) * 60 +
      Number.parseFloat(parts[2] || "0")
    );
  }
  if (parts.length === 2) {
    return Number.parseInt(parts[0] || "0", 10) * 60 + Number.parseFloat(parts[1] || "0");
  }
  return Number.parseFloat(timestamp) || 0;
}

/**
 * Parse a single cue from VTT lines starting at given index
 */
function parseSingleCue(
  lines: string[],
  startIdx: number,
  cueIndex: number,
): { cue: ThumbnailCue | null; nextIdx: number } {
  const cueRegex = /^([\d:.]+)\s+-->\s+([\d:.]+)$/;
  const line = lines[startIdx]?.trim();

  if (!line) return { cue: null, nextIdx: startIdx + 1 };
  if (/^\d+$/.test(line)) return { cue: null, nextIdx: startIdx + 1 };

  const match = cueRegex.exec(line);
  if (!match) return { cue: null, nextIdx: startIdx + 1 };

  const startTime = parseTimestamp(match[1] || "");
  const endTime = parseTimestamp(match[2] || "");
  const urlLine = lines[startIdx + 1]?.trim();

  if (!urlLine) return { cue: null, nextIdx: startIdx + 2 };

  const [imageUrl, fragment] = urlLine.split("#xywh=");
  if (!imageUrl || !fragment) return { cue: null, nextIdx: startIdx + 2 };

  const [x, y, w, h] = fragment.split(",").map(Number);
  if (x === undefined || y === undefined || w === undefined || h === undefined) {
    return { cue: null, nextIdx: startIdx + 2 };
  }

  return {
    cue: { index: cueIndex, startTime, endTime, imageUrl, coords: { x, y, w, h } },
    nextIdx: startIdx + 2,
  };
}

/**
 * Parse WebVTT file to extract thumbnail cues
 */
function parseVTT(text: string): ThumbnailCue[] {
  const cues: ThumbnailCue[] = [];
  const lines = text.split("\n");
  let i = 0;

  // Skip WEBVTT header until we find a timestamp line
  const timestampRegex = /^\d+:\d+:\d+\.\d+\s+-->\s+\d+:\d+:\d+\.\d+$/;
  while (i < lines.length && !timestampRegex.test(lines[i]?.trim() ?? "")) {
    i++;
  }

  // Parse cues
  while (i < lines.length) {
    const { cue, nextIdx } = parseSingleCue(lines, i, cues.length);
    if (cue) cues.push(cue);
    i = nextIdx;
  }

  return cues;
}

/**
 * Detect number of columns from first row of cues
 * (when x-coordinate resets to 0, we've wrapped to next row)
 */
function detectColumns(cues: ThumbnailCue[]): number {
  if (cues.length <= 1) return DEFAULT_SPRITE_COLUMNS;
  let cols = 1;
  for (let i = 1; i < cues.length; i++) {
    if (cues[i].coords.x === 0) break;
    cols++;
  }
  return cols;
}

/**
 * Preload sprite images for smoother scrubbing
 * Uses staggered loading to avoid flooding the connection pool
 */
function preloadSprites(cues: ThumbnailCue[], onLoad: (url: string) => void): void {
  const uniqueImages = Array.from(new Set(cues.map((c) => c.imageUrl)));

  // Stagger image loading to avoid blocking the main thread
  // Load one image at a time with a small delay between each
  let index = 0;
  const loadNext = (): void => {
    if (index >= uniqueImages.length) return;
    const url = uniqueImages[index];
    if (!url) {
      index++;
      loadNext();
      return;
    }
    const img = new Image();
    img.onload = () => {
      onLoad(url);
      index++;
      // Use requestIdleCallback if available, otherwise setTimeout
      if (typeof globalThis.requestIdleCallback === "function") {
        globalThis.requestIdleCallback(loadNext);
      } else {
        globalThis.setTimeout(loadNext, 50);
      }
    };
    img.onerror = () => {
      index++;
      loadNext();
    };
    img.src = url;
  };

  // Start loading first image
  loadNext();
}

export type UseThumbnailSpriteResult = {
  /** Parsed cues from VTT file */
  cues: ThumbnailCue[];
  /** Whether VTT is currently loading */
  loading: boolean;
  /** Whether VTT failed to load */
  error: boolean;
  /** Set of preloaded sprite image URLs */
  preloadedSprites: Set<string>;
  /** Detected number of columns in sprite sheet */
  spriteColumns: number;
  /** Find cue for a given time in seconds */
  findCue: (time: number) => ThumbnailCue | null;
  /** Whether HD quality was requested but fell back to standard */
  usingFallback: boolean;
};

/**
 * Transform VTT URL to HD variant
 * e.g., "thumbnails.vtt" → "thumbnails-hd.vtt"
 */
function getHdVttUrl(vttUrl: string): string {
  return vttUrl.replace(/\.vtt$/, "-hd.vtt");
}

/**
 * Hook to defer execution until after a delay AND browser is idle
 * Combines setTimeout delay with requestIdleCallback for best results
 *
 * @param defer - Whether to defer at all
 * @param delayMs - Minimum delay before checking idle state (default: 500ms)
 * @returns true when ready to proceed
 */
function useDeferUntilIdle(defer: boolean, delayMs = 500): boolean {
  const [ready, setReady] = useState(!defer);

  useEffect(() => {
    if (!defer || ready) return;

    let cancelled = false;
    let idleHandle: number | ReturnType<typeof setTimeout>;

    // First: wait for minimum delay (let page transition complete)
    const delayHandle = globalThis.setTimeout(() => {
      if (cancelled) return;

      // Then: wait for browser idle time
      if (typeof globalThis.requestIdleCallback === "function") {
        idleHandle = globalThis.requestIdleCallback(
          () => {
            if (!cancelled) setReady(true);
          },
          { timeout: 2000 },
        );
      } else {
        // Safari fallback: just use another small timeout
        idleHandle = globalThis.setTimeout(() => {
          if (!cancelled) setReady(true);
        }, 50);
      }
    }, delayMs);

    return () => {
      cancelled = true;
      globalThis.clearTimeout(delayHandle);
      if (typeof globalThis.cancelIdleCallback === "function" && typeof idleHandle === "number") {
        globalThis.cancelIdleCallback(idleHandle);
      } else if (idleHandle) {
        globalThis.clearTimeout(idleHandle as ReturnType<typeof setTimeout>);
      }
    };
  }, [defer, ready, delayMs]);

  return ready;
}

/**
 * Hook for fetching and parsing thumbnail sprite VTT data
 *
 * @param vttUrl - URL to the VTT file (from asset.thumbnailVttUrl)
 * @param quality - Quality level: 'standard' (320×180) or 'hd' (480×270). Defaults to 'standard'.
 * @param options - Additional options
 * @param options.defer - If true, delays fetch until browser is idle (avoids blocking transitions)
 * @param options.skipPreload - If true, skips automatic sprite image preloading (use with preloadThumbnailSprite)
 * @returns Parsed cue data and helper functions
 */
export function useThumbnailSprite(
  vttUrl: string | null,
  quality: ThumbnailQuality = "standard",
  options?: { defer?: boolean; skipPreload?: boolean },
): UseThumbnailSpriteResult {
  const [cues, setCues] = useState<ThumbnailCue[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [preloadedSprites, setPreloadedSprites] = useState<Set<string>>(new Set());
  const [spriteColumns, setSpriteColumns] = useState(DEFAULT_SPRITE_COLUMNS);
  const [usingFallback, setUsingFallback] = useState(false);
  const deferReady = useDeferUntilIdle(options?.defer ?? false);

  useEffect(() => {
    if (!deferReady) return;

    if (!vttUrl) {
      setCues([]);
      setLoading(false);
      setError(false);
      setUsingFallback(false);
      return;
    }

    setLoading(true);
    setError(false);
    setUsingFallback(false);

    const handlePreload = (url: string): void => {
      setPreloadedSprites((prev) => new Set(prev).add(url));
    };

    const processVttResponse = (text: string): void => {
      const parsedCues = parseVTT(text);
      setCues(parsedCues);
      setSpriteColumns(detectColumns(parsedCues));
      setLoading(false);
      // Only preload sprites if not explicitly skipped
      // Player uses skipPreload + preloadThumbnailSprite for better timing control
      if (!options?.skipPreload) {
        preloadSprites(parsedCues, handlePreload);
      }
    };

    const fetchVtt = async (): Promise<void> => {
      // For HD quality, try HD URL first, then fallback to standard
      if (quality === "hd") {
        const hdUrl = getHdVttUrl(vttUrl);
        try {
          const hdRes = await fetch(hdUrl);
          if (hdRes.ok) {
            const text = await hdRes.text();
            processVttResponse(text);
            return;
          }
          // HD not available, fall through to standard
        } catch {
          // HD fetch failed, fall through to standard
        }
        setUsingFallback(true);
      }

      // Fetch standard quality (or fallback from HD)
      try {
        const res = await fetch(vttUrl);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const text = await res.text();
        processVttResponse(text);
      } catch (err) {
        console.error("[useThumbnailSprite] Failed to load VTT:", vttUrl, err);
        setError(true);
        setLoading(false);
      }
    };

    void fetchVtt();
  }, [vttUrl, quality, deferReady]);

  const findCue = useCallback(
    (time: number): ThumbnailCue | null => {
      if (!cues.length) return null;
      // Find cue containing the time
      const cue = cues.find((c) => time >= c.startTime && time < c.endTime);
      if (cue) return cue;
      // Fallback: if past last cue, return last frame
      const lastCue = cues.at(-1);
      if (lastCue && time >= lastCue.startTime) return lastCue;
      // Fallback: return first frame
      return cues[0] ?? null;
    },
    [cues],
  );

  return {
    cues,
    loading,
    error,
    preloadedSprites,
    spriteColumns,
    findCue,
    usingFallback,
  };
}

/**
 * Format seconds as MM:SS or H:MM:SS
 */
export function formatTime(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
  return `${m}:${String(r).padStart(2, "0")}`;
}

/** Cache for preloaded VTT data to avoid duplicate fetches */
const preloadCache = new Map<string, { cues: ThumbnailCue[]; sprites: Set<string> }>();

/**
 * Preload thumbnail sprite VTT and images in the background (non-React)
 *
 * Call this early (e.g., after video starts playing) to ensure thumbnails
 * are ready before the user hovers over the timeline.
 *
 * @param vttUrl - URL to the VTT file (from asset.thumbnailVttUrl)
 * @returns Promise that resolves when VTT and sprites are preloaded
 *
 * @example
 * ```tsx
 * // In player component, after video starts
 * useEffect(() => {
 *   if (asset?.thumbnailVttUrl && isPlaying) {
 *     const timer = setTimeout(() => {
 *       preloadThumbnailSprite(asset.thumbnailVttUrl);
 *     }, 2000); // Defer to avoid competing with HLS buffering
 *     return () => clearTimeout(timer);
 *   }
 * }, [asset?.thumbnailVttUrl, isPlaying]);
 * ```
 */
export async function preloadThumbnailSprite(vttUrl: string): Promise<void> {
  // Skip if already preloaded
  if (preloadCache.has(vttUrl)) {
    return;
  }

  try {
    const res = await fetch(vttUrl);
    if (!res.ok) {
      console.warn("[preloadThumbnailSprite] Failed to fetch VTT:", vttUrl);
      return;
    }

    const text = await res.text();
    const cues = parseVTT(text);

    if (cues.length === 0) {
      console.warn("[preloadThumbnailSprite] No cues found in VTT:", vttUrl);
      return;
    }

    // Preload unique sprite images
    const uniqueImages = new Set(cues.map((c) => c.imageUrl));
    const loadedSprites = new Set<string>();

    // Simple image preload - just load into browser cache
    const preloadPromises = Array.from(uniqueImages).map(
      (url) =>
        new Promise<void>((resolve) => {
          const img = new Image();
          img.onload = () => {
            loadedSprites.add(url);
            resolve();
          };
          img.onerror = () => resolve();
          img.src = url;
        }),
    );

    await Promise.all(preloadPromises);

    // Cache the result
    preloadCache.set(vttUrl, { cues, sprites: loadedSprites });
    console.log(`[preloadThumbnailSprite] Preloaded ${cues.length} cues, ${loadedSprites.size} sprites for:`, vttUrl);
  } catch (err) {
    console.warn("[preloadThumbnailSprite] Error preloading:", vttUrl, err);
  }
}

/**
 * Check if a VTT URL has been preloaded
 */
export function isThumbnailSpritePreloaded(vttUrl: string): boolean {
  return preloadCache.has(vttUrl);
}
